
import { Link } from 'react-router-dom';
import { Home, ArrowLeft, AlertTriangle, Search, Phone, Mail } from 'lucide-react';

interface ErrorPageProps {
  errorCode?: string;
  title?: string;
  message?: string;
  showBackButton?: boolean;
  showHomeButton?: boolean;
}

export default function ErrorPage({ 
  errorCode = "404",
  title = "Página no encontrada",
  message = "Lo sentimos, la página que buscas no existe o ha sido movida.",
  showBackButton = true,
  showHomeButton = true
}: ErrorPageProps) {
  
  const handleGoBack = () => {
    window.history.back();
  };

  const getErrorIllustration = () => {
    switch (errorCode) {
      case "404":
        return (
          <div className="relative">
            {/* Círculos decorativos con colores del brand */}
            <div className="absolute -top-8 -left-8 w-32 h-32 bg-neuro-blue/20 rounded-full animate-pulse"></div>
            <div className="absolute -bottom-8 -right-8 w-24 h-24 bg-neuro-yellow/20 rounded-full animate-pulse delay-1000"></div>
            <div className="absolute top-4 right-4 w-16 h-16 bg-neuro-red/20 rounded-full animate-pulse delay-500"></div>
            
            {/* Ilustración principal */}
            <div className="relative z-10 text-center">
              <div className="w-48 h-48 mx-auto mb-8 relative">
                {/* Fondo del 404 */}
                <div className="absolute inset-0 bg-gradient-to-br from-neuro-blue/10 to-neuro-dark-blue/10 rounded-full flex items-center justify-center">
                  <div className="text-9xl font-bold text-neuro-blue/30">404</div>
                </div>
                
                {/* Icono de búsqueda superpuesto */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <Search className="w-24 h-24 text-neuro-red/60" />
                </div>
                
                {/* Punto de exclamación */}
                <div className="absolute -top-4 -right-4 w-12 h-12 bg-neuro-yellow rounded-full flex items-center justify-center animate-bounce">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        );
      
      case "500":
        return (
          <div className="relative">
            <div className="w-48 h-48 mx-auto mb-8 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-neuro-red/10 to-red-600/10 rounded-full flex items-center justify-center">
                <div className="text-9xl font-bold text-neuro-red/30">500</div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <AlertTriangle className="w-24 h-24 text-neuro-red/60" />
              </div>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="relative">
            <div className="w-48 h-48 mx-auto mb-8 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-neuro-yellow/10 to-yellow-500/10 rounded-full flex items-center justify-center">
                <div className="text-6xl font-bold text-neuro-yellow/30">{errorCode}</div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <AlertTriangle className="w-24 h-24 text-neuro-yellow/60" />
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neuro-light-gray to-white flex items-center justify-center p-4">
      {/* Fondo decorativo */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-neuro-blue/5 rounded-full"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-neuro-green/5 rounded-full"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-neuro-yellow/3 rounded-full"></div>
      </div>

      {/* Contenido principal */}
      <div className="relative z-10 max-w-2xl mx-auto text-center">
        {/* Ilustración del error */}
        {getErrorIllustration()}

        {/* Información del error */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-6xl font-bold text-neuro-dark-blue mb-6">
            {title}
          </h1>
          
          <p className="text-xl text-gray-600 mb-8 max-w-lg mx-auto leading-relaxed">
            {message}
          </p>

          {/* Información adicional según el tipo de error */}
          {errorCode === "404" && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-blue-800 mb-3">
                ¿Qué puedes hacer?
              </h3>
              <ul className="text-blue-700 text-left space-y-2">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  Verificar que la URL esté escrita correctamente
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  Usar el menú de navegación para encontrar la página
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  Contactarnos directamente si necesitas ayuda
                </li>
              </ul>
            </div>
          )}

          {errorCode === "500" && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-red-800 mb-3">
                Error del servidor
              </h3>
              <p className="text-red-700">
                Estamos experimentando problemas técnicos. Por favor, inténtalo de nuevo en unos minutos o contáctanos si el problema persiste.
              </p>
            </div>
          )}
        </div>

        {/* Botones de acción */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          {showBackButton && (
            <button
              onClick={handleGoBack}
              className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium"
            >
              <ArrowLeft className="w-5 h-5" />
              Volver atrás
            </button>
          )}

          {showHomeButton && (
            <Link
              to="/"
              className="flex items-center gap-2 px-6 py-3 bg-neuro-blue text-white rounded-lg hover:bg-neuro-dark-blue transition-colors duration-300 font-medium"
            >
              <Home className="w-5 h-5" />
              Ir al inicio
            </Link>
          )}
        </div>

        {/* Información de contacto */}
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
          <h3 className="text-xl font-semibold text-neuro-dark-blue mb-4">
            ¿Necesitas ayuda?
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-neuro-green/20 rounded-full flex items-center justify-center">
                <Phone className="w-6 h-6 text-neuro-green" />
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-900">Teléfono</p>
                <p className="text-gray-600">+506 8583-8056</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-neuro-red/20 rounded-full flex items-center justify-center">
                <Mail className="w-6 h-6 text-neuro-red" />
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-900">Email</p>
                <p className="text-gray-600">info@neuropequescr.com</p>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Si continúas experimentando problemas, no dudes en contactarnos. 
              Estamos aquí para ayudarte.
            </p>
          </div>
        </div>

        {/* Enlaces útiles */}
        <div className="mt-8">
          <p className="text-gray-500 mb-4">Enlaces útiles:</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/servicios"
              className="text-neuro-blue hover:text-neuro-dark-blue transition-colors"
            >
              Nuestros Servicios
            </Link>
            <Link
              to="/quienes-somos"
              className="text-neuro-blue hover:text-neuro-dark-blue transition-colors"
            >
              Quiénes Somos
            </Link>
            <Link
              to="/contacto"
              className="text-neuro-blue hover:text-neuro-dark-blue transition-colors"
            >
              Contacto
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
